// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useEffect, useRef, useState } from 'react';
import { hapticLight } from '../../native/haptics.js';

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  // The scroll container this indicator lives in; pull only engages at scrollTop 0.
  scrollRef: React.RefObject<HTMLDivElement>;
}

const THRESHOLD = 64;

// DECISION (Phase 4): a self-contained pull-to-refresh that attaches touch
// listeners to its parent scroll container. It only starts a pull when the
// container is already scrolled to the top, so it never hijacks normal upward
// scrolling, and it triggers the shared ledger sync. Lives as a sibling at the
// top of the scroll region and translates a small status label into view.
export function PullToRefresh({ onRefresh, scrollRef }: PullToRefreshProps) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef<number | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    function onStart(e: TouchEvent) {
      if (el && el.scrollTop <= 0 && !refreshing) startY.current = e.touches[0]!.clientY;
      else startY.current = null;
    }
    function onMove(e: TouchEvent) {
      if (startY.current === null) return;
      const dy = e.touches[0]!.clientY - startY.current;
      if (dy > 0) {
        // Resist: the label lags the finger for a rubber-band feel.
        setPull(Math.min(THRESHOLD * 1.5, dy * 0.5));
      }
    }
    async function onEnd() {
      if (startY.current === null) return;
      const shouldRefresh = pull >= THRESHOLD;
      startY.current = null;
      if (shouldRefresh) {
        setRefreshing(true);
        setPull(THRESHOLD);
        await hapticLight();
        try {
          await onRefresh();
        } finally {
          setRefreshing(false);
          setPull(0);
        }
      } else {
        setPull(0);
      }
    }

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: true });
    el.addEventListener('touchend', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
    };
  }, [scrollRef, pull, refreshing, onRefresh]);

  if (pull <= 0 && !refreshing) return null;

  return (
    <div
      className="flex items-center justify-center text-xs text-text-muted overflow-hidden"
      style={{ height: pull }}
    >
      {refreshing ? 'Syncing…' : pull >= THRESHOLD ? 'Release to sync' : 'Pull to sync'}
    </div>
  );
}
